import CategoryManagerOverviewCard from '@/components/CategoryManagerOverviewCard';
import SortableCategoryManagerOverviewCard from '@/components/CategoryManagerOverviewCard/SortableCategoryManagerOverviewCard';
import CreateEditCategoryModal from '@/components/CreateEditCategoryModal';
import DeleteArchiveCategoryModal from '@/components/DialogModal';
import ProjectSelector from '@/components/ProjectSelector/ProjectSelector';
import { useAnimatePlanPages } from '@/contexts/AnimatedPlanPages';
import { CronofySyncEventPayload } from '@/gql/cronofy/types';
import { keys } from '@/gql/global/keys';
import { ARCHIVE_PROJECT, RESTORE_PROJECT } from '@/gql/project';
import { cleanCategoryForMutation } from '@/services/categories';
import {
  useCategories,
  useDeleteCategory,
  useUpdateCategoriesOrder,
  useUpdateCategoryMutation,
} from '@/services/categories/hooks';
import { useCronofySyncEventMany } from '@/services/cronofy/hooks';
import { fetchData } from '@/services/graphql';
import { useProjects } from '@/services/project/hooks';
import { IconChevronDown, IconChevronRight, IconPlus } from '@/theme/icons';
import { Category } from '@/types/category';
import { refetchCategoryAndBlocks } from '@/utils/category';
import { pageTransition } from '@/utils/pageAnimation';
import rem from '@/utils/rem';
import { invalidateQueries, refetchQueries } from '@/utils/tanStackQuery';
import { trackCreateCategorySelected } from '@/utils/tracking';
import { Box, Button, Collapse, Flex, Grid, Icon, Text, useDisclosure, useToast } from '@chakra-ui/react';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, rectSortingStrategy } from '@dnd-kit/sortable';
import { motion } from 'framer-motion';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DashboardLayout from 'src/layouts/Dashboard';

export const refetchQueriesForArchive = () => {
  refetchCategoryAndBlocks();
  refetchQueries([
    keys.project.all.queryKey,
    keys.categoryEvents.list._def,
    keys.actions.all.queryKey,
    keys.actions.leveragedAndCommitted.queryKey,
  ]);
};

const CategoryManagerPage = () => {
  const { t } = useTranslation('CategoryManager');
  const { pageNavigationEffect } = useAnimatePlanPages();

  const { data: categoriesList } = useCategories();

  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [openArchiveModal, setOpenArchiveModal] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [categoryInfo, setCategoryInfo] = useState<Category>();
  const [isDropdownOpen, setDropdownOpen] = useState(true);
  const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [localCategories, setLocalCategories] = useState<Category[] | null>(null);

  const { data: projects } = useProjects({
    refetchOnMount: true,
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  const handleToggle = (projectId: string) => {
    setSelectedProjects((prev) =>
      prev.includes(projectId) ? prev.filter((id) => id !== projectId) : [...prev, projectId],
    );
  };

  const toast = useToast();

  const {
    isOpen: isCreateCategoryOpen,
    onOpen: onCreateCategoryOpen,
    onClose: onCreateCategoryClose,
  } = useDisclosure({ id: 'createNewCategory' });

  const filteredProjects = useMemo(() => {
    return projects?.project.filter((project) => project.categoryId === categoryInfo?.id);
  }, [projects, categoryInfo?.id]);

  const { mutateAsync: updateCategoriesOrder } = useUpdateCategoriesOrder();
  const { mutate: cronofySyncEventMany } = useCronofySyncEventMany();

  useEffect(() => {
    if (categoriesList?.category) {
      if (!localCategories) {
        setLocalCategories(categoriesList.category);
      } else {
        const mergedCategories = categoriesList.category.map((serverCategory) => {
          const localCategory = localCategories.find((local) => local.id === serverCategory.id);
          if (localCategory) {
            return {
              ...serverCategory,
              order: localCategory.order,
            };
          }
          return serverCategory;
        });
        setLocalCategories(mergedCategories);
      }
    }
  }, [categoriesList?.category, localCategories]);

  const updateCategory = useUpdateCategoryMutation({
    onSuccess: async (data) => {
      const isArchive = data.updateCategoryByPk.isArchived;
      const categoryId = data.updateCategoryByPk.id;
      const updatedCategory = data.updateCategoryByPk;

      if (localCategories) {
        const updatedLocalCategories = localCategories.map((c) => {
          if (c.id === categoryId) {
            return {
              ...c,
              ...updatedCategory,
              order: c.order,
            };
          }
          return c;
        });
        setLocalCategories(updatedLocalCategories);
      }

      if (filteredProjects && filteredProjects.length > 0) {
        if (isArchive) {
          const projectsPayload = {
            categoryId: data.updateCategoryByPk.id,
            isArchived: isArchive,
          };

          await fetchData<{ isArchived: boolean; categoryId: string }>(ARCHIVE_PROJECT, projectsPayload);
        } else {
          const projectsPayload = {
            ids: selectedProjects,
            isArchived: isArchive,
          };
          await fetchData<{ isArchived: boolean; ids: [string] }>(RESTORE_PROJECT, projectsPayload);
        }
      }

      if (data.updateCategoryByPk.actions) {
        const eventsToSync: CronofySyncEventPayload[] = data.updateCategoryByPk.actions
          .filter((action) => !action.isCanceled && action.scheduledDate && action.scheduledTime)
          .map((action) => ({
            eventId: action.id,
            eventType: 'Action',
            deleted: isArchive,
          }));

        if (eventsToSync.length) {
          cronofySyncEventMany({ events: eventsToSync });
        }
      }

      refetchQueries([keys.project.category(categoryId)]);
      refetchQueriesForArchive();

      toast({
        title: t(`toasts.category${isArchive ? 'Archived' : 'Restored'}`),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      setCategoryInfo(undefined);
      setOpenArchiveModal(false);
    },
  });

  const deleteCategory = useDeleteCategory({
    onSuccess: () => {
      refetchCategoryAndBlocks();

      invalidateQueries([keys.project.all.queryKey, keys.categoryEvents.list._def]);

      toast({
        title: t('toasts.categoryDeleted'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      setCategoryInfo(undefined);
      setOpenDeleteModal(false);
    },
  });

  const onDeleteCategory = useCallback(async () => {
    if (!categoryInfo || !localCategories) return;

    const updatedLocalCategories = localCategories.filter((c) => c.id !== categoryInfo.id);
    setLocalCategories(updatedLocalCategories);

    setOpenDeleteModal(false);
    setCategoryInfo(undefined);

    toast({
      title: t('toasts.categoryDeleted'),
      status: 'success',
      duration: 3000,
      isClosable: true,
    });

    deleteCategory.mutateAsync({
      categoryId: categoryInfo.id,
    });
  }, [deleteCategory, categoryInfo, localCategories, toast, t]);

  const onClose = useCallback(
    (updatedOrNewCategory?: Category) => {
      if (updatedOrNewCategory && localCategories) {
        if (isEdit) {
          const updatedLocalCategories = localCategories.map((c) => {
            if (c.id === updatedOrNewCategory.id) {
              return {
                ...c,
                ...updatedOrNewCategory,
                order: c.order,
              };
            }
            return c;
          });
          setLocalCategories(updatedLocalCategories);
        } else {
          const newLocalCategories = [...localCategories, updatedOrNewCategory];
          setLocalCategories(newLocalCategories);
        }
      }

      setCategoryInfo(undefined);
      setIsEdit(false);
      onCreateCategoryClose();
    },
    [isEdit, localCategories, onCreateCategoryClose],
  );

  useEffect(() => {
    if (categoryInfo && isEdit) {
      onCreateCategoryOpen();
    }
  }, [categoryInfo, isEdit, onCreateCategoryOpen]);

  const rightSectionElements = (
    <Button
      height="full"
      marginLeft="auto"
      padding={rem(8)}
      borderWidth={rem(1)}
      borderStyle="solid"
      borderColor="stroke-primary"
      borderRadius={rem(4)}
      backgroundColor="background-primary"
      leftIcon={<Icon as={IconPlus} color="text-primary" />}
      onClick={() => {
        setCategoryInfo(undefined);
        setIsEdit(false);
        onCreateCategoryOpen();
        trackCreateCategorySelected('category manager');
      }}
      variant="secondary"
    >
      <Text textStyle="small" color="text-primary" fontWeight={900}>
        {t('buttons.createNewCategory')}
      </Text>
    </Button>
  );

  const [activeCategories, archiveCategories] = useMemo(() => {
    const categoriesToUse = localCategories || categoriesList?.category;
    if (!categoriesToUse) return [[], []];

    const active = categoriesToUse.filter((c) => !c.isArchived).sort((a, b) => (a.order || 0) - (b.order || 0));
    const archive = categoriesToUse.filter((c) => c.isArchived).sort((a, b) => (a.order || 0) - (b.order || 0));

    return [active, archive];
  }, [localCategories, categoriesList]);

  const archiveActiveCategory = useCallback(async () => {
    if (!categoryInfo || !localCategories) return;

    let newOrder: number;
    if (categoryInfo.isArchived) {
      newOrder = Math.max(0, ...activeCategories.map((c) => c.order || 0)) + 1;
    } else {
      newOrder = Math.min(1, ...archiveCategories.map((c) => c.order || 0));
      if (newOrder > 1) newOrder = 1;
    }

    const updatedLocalCategories = localCategories.map((c) =>
      c.id === categoryInfo.id ? { ...c, isArchived: !c.isArchived, order: newOrder } : c,
    );
    setLocalCategories(updatedLocalCategories);

    setOpenArchiveModal(false);
    setCategoryInfo(undefined);

    updateCategory.mutateAsync({
      ...cleanCategoryForMutation(categoryInfo),
      isArchived: !categoryInfo.isArchived,
      order: newOrder,
    });
  }, [updateCategory, categoryInfo, localCategories, activeCategories, archiveCategories]);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  }, []);

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      setActiveId(null);

      if (!over || active.id === over.id) {
        return;
      }

      const activeCategoryId = active.id as string;
      const overCategoryId = over.id as string;

      const activeInActiveCategories = activeCategories.some((c) => c.id === activeCategoryId);
      const overInActiveCategories = activeCategories.some((c) => c.id === overCategoryId);

      if (activeInActiveCategories !== overInActiveCategories) {
        return;
      }

      const categoriesToReorder = activeInActiveCategories ? activeCategories : archiveCategories;
      const oldIndex = categoriesToReorder.findIndex((c) => c.id === activeCategoryId);
      const newIndex = categoriesToReorder.findIndex((c) => c.id === overCategoryId);

      if (oldIndex === newIndex) {
        return;
      }

      const reorderedCategories = arrayMove(categoriesToReorder, oldIndex, newIndex);
      const updatedReorderedCategories = reorderedCategories.map((category, index) => ({
        ...category,
        order: index + 1,
      }));

      const otherCategories = activeInActiveCategories ? archiveCategories : activeCategories;
      const newLocalCategories = [...updatedReorderedCategories, ...otherCategories];
      setLocalCategories(newLocalCategories);

      const updatedCategories = updatedReorderedCategories.map((category, index) => ({
        _set: { order: index + 1 },
        where: { id: { _eq: category.id } },
      }));

      try {
        await updateCategoriesOrder({ updates: updatedCategories });
      } catch (error) {
        console.error('Failed to update category order:', error);
        toast({
          title: t('toasts.categoryOrderUpdateFailed'),
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    },
    [activeCategories, archiveCategories, updateCategoriesOrder, toast, t],
  );

  return (
    <DashboardLayout pageTitle={t('pageTitle')} rightSectionElements={rightSectionElements}>
      <motion.div
        className="row"
        initial="initial"
        animate="in"
        exit="out"
        variants={pageNavigationEffect}
        transition={pageTransition}
      >
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          {/* Active Categories Section */}
          <Box overflowY="auto" width="full" minHeight="42vh" borderRadius="md">
            <SortableContext items={activeCategories.map((c) => c.id)} strategy={rectSortingStrategy}>
              <Grid
                alignItems={activeCategories.length === 0 ? 'center' : 'start'}
                justifyContent={activeCategories.length === 0 ? 'center' : 'start'}
                gridRowGap={rem(16)}
                gridColumnGap={rem(16)}
                gridTemplateColumns={activeCategories.length === 0 ? '1fr' : 'repeat(auto-fill, minmax(400px, 1fr))'}
                width="full"
                paddingBottom={rem(32)}
              >
                {activeCategories.length > 0 ? (
                  activeCategories.map((category) => (
                    <SortableCategoryManagerOverviewCard
                      key={category.id}
                      category={category}
                      isDragging={activeId === category.id}
                      setOpenDeleteModal={() => {
                        setCategoryInfo(category);
                        setOpenDeleteModal(true);
                      }}
                      setOpenArchiveModal={() => {
                        setCategoryInfo(category);
                        setOpenArchiveModal(true);
                      }}
                      setOpenEditModal={() => {
                        setCategoryInfo(category);
                        setIsEdit(true);
                      }}
                    />
                  ))
                ) : (
                  <Text color="gray.500" fontSize="lg" textAlign="center">
                    {t('emptyStates.noActiveCategories')}
                  </Text>
                )}
              </Grid>
            </SortableContext>
          </Box>

          {/* Archive Categories Dropdown Section */}
          <Flex
            alignItems="center"
            gap={2}
            width="full"
            marginBottom={rem(20)}
            background="background-tertiary"
            borderRadius="md"
            cursor="pointer"
            onClick={() => setDropdownOpen(!isDropdownOpen)}
            paddingX={4}
            paddingY={3}
          >
            <Text color="text-primary" fontWeight="800">
              {t('sections.archivedCategories')}
            </Text>
            <Icon
              as={isDropdownOpen ? IconChevronDown : IconChevronRight}
              width={rem(14)}
              height={rem(14)}
              color="text-primary"
            />
          </Flex>

          {/* Collapse for Archive Categories */}
          <Collapse animateOpacity in={isDropdownOpen}>
            <SortableContext items={archiveCategories.map((c) => c.id)} strategy={rectSortingStrategy}>
              <Grid
                alignItems={archiveCategories.length === 0 ? 'center' : 'start'}
                justifyContent={archiveCategories.length === 0 ? 'center' : 'start'}
                gridRowGap={rem(16)}
                gridColumnGap={rem(16)}
                gridTemplateColumns={archiveCategories.length === 0 ? '1fr' : 'repeat(auto-fill, minmax(400px, 1fr))'}
                width="full"
                marginBottom={6}
              >
                {archiveCategories.length > 0 ? (
                  archiveCategories.map((category) => (
                    <SortableCategoryManagerOverviewCard
                      key={category.id}
                      category={category}
                      isDragging={activeId === category.id}
                      setOpenDeleteModal={() => {
                        setCategoryInfo(category);
                        setOpenDeleteModal(true);
                      }}
                      setOpenArchiveModal={() => {
                        setCategoryInfo(category);
                        setOpenArchiveModal(true);
                      }}
                      setOpenEditModal={() => {
                        setCategoryInfo(category);
                        setIsEdit(true);
                      }}
                    />
                  ))
                ) : (
                  <Text color="gray.500" fontSize="lg" textAlign="center">
                    {t('emptyStates.noArchivedCategories')}
                  </Text>
                )}
              </Grid>
            </SortableContext>
          </Collapse>

          {/* Drag Overlay */}
          <DragOverlay>
            {activeId ? (
              <Box opacity={0.8}>
                <CategoryManagerOverviewCard
                  category={[...activeCategories, ...archiveCategories].find((c) => c.id === activeId)!}
                  setOpenDeleteModal={() => {}}
                  setOpenArchiveModal={() => {}}
                  setOpenEditModal={() => {}}
                />
              </Box>
            ) : null}
          </DragOverlay>
        </DndContext>
      </motion.div>

      {/* Create/Edit Category Modal */}
      <CreateEditCategoryModal
        key={isEdit ? `edit-${categoryInfo?.id}` : 'create-new'}
        category={isEdit ? categoryInfo : undefined}
        isOpen={isCreateCategoryOpen}
        onClose={onClose}
        showEntityModalOnCreate
        isEditMode={isEdit}
      />

      {/* Delete Category Modal */}
      {openDeleteModal && (
        <DeleteArchiveCategoryModal
          isOpen={openDeleteModal}
          title={t('modal.deleteCategory.title')}
          message={t('modal.deleteCategory.message')}
          confirmText={t('modal.deleteCategory.confirmText')}
          cancelText={t('modal.deleteCategory.cancelText')}
          confirmVariant="danger"
          onCancel={() => {
            setCategoryInfo(undefined);
            setOpenDeleteModal(false);
          }}
          onConfirm={onDeleteCategory}
        />
      )}

      {/* Archive/Restore Category Modal */}
      {openArchiveModal && (
        <DeleteArchiveCategoryModal
          isOpen={openArchiveModal}
          title={t(`modal.archiveRestoreCategory.titles.${categoryInfo?.isArchived ? 'restore' : 'archive'}`)}
          message={
            categoryInfo?.isArchived && filteredProjects && filteredProjects?.length > 0
              ? t('modalMessages.categoryRestoreConfirmation')
              : categoryInfo?.isArchived
                ? t('modalMessages.categoryWithoutProjectsConfirmation')
                : t('modalMessages.categoryArchiveConfirmation')
          }
          confirmText={t(
            `modal.archiveRestoreCategory.confirmTexts.${categoryInfo?.isArchived ? 'restore' : 'archive'}`,
          )}
          cancelText={t('modal.archiveRestoreCategory.cancelText')}
          confirmVariant="primary"
          onCancel={() => {
            setCategoryInfo(undefined);
            setOpenArchiveModal(false);
          }}
          onConfirm={archiveActiveCategory}
        >
          {categoryInfo?.isArchived && filteredProjects && filteredProjects.length > 0 ? (
            <ProjectSelector onToggle={handleToggle} projects={filteredProjects} selectedProjects={selectedProjects} />
          ) : undefined}
        </DeleteArchiveCategoryModal>
      )}
    </DashboardLayout>
  );
};

export default CategoryManagerPage;
